import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import Intercom, { shutdown } from '@intercom/messenger-js-sdk';
import { Actions, ofType } from '@ngrx/effects';
import { APP_ENVIRONMENT } from '@qtek/core/api-core';
import { AuthActions, IntegrationsActions, UserStore } from '@qtek/libs/store';
import { CompanyOutput, PersonOutput, RelUserModel } from '@qtek/shared/models';

const INTERCOM_TOKEN_KEY = 'intercom_user_jwt';

@Injectable({ providedIn: 'root' })
export class IntercomService {
  private readonly actions$ = inject(Actions);
  private readonly userStore = inject(UserStore);
  private readonly environment = inject(APP_ENVIRONMENT);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    const appId = this.environment.intercomAppId;
    if (!this.environment.intercomEnabled || !appId) {
      return;
    }

    this.actions$
      .pipe(
        ofType(IntegrationsActions.getIntercomTokenSuccess),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ token }) => {
        const person = this.userStore.currentPerson();
        const company = this.userStore.mainCompany();
        const userRelation = this.userStore.userRelation();
        if (!person || !token) return;
        this.initIntercom(appId, token, person, company, userRelation);
      });

    this.actions$
      .pipe(ofType(AuthActions.logout), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        sessionStorage.removeItem(INTERCOM_TOKEN_KEY);
        shutdown();
      });
  }

  private initIntercom(
    appId: string,
    token: string,
    person: PersonOutput,
    company: CompanyOutput | null,
    userRelation: RelUserModel | null
  ): void {
    Intercom({
      app_id: appId,
      user_id: userRelation?._id,
      name:
        `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim() ||
        person.name,
      email: userRelation?.email,
      ...(company?.createTs
        ? { created_at: Math.floor(company.createTs / 1000) }
        : {}),
      intercom_user_jwt: token,
    });
  }
}
